let logRocketInstance: any = null;
let logRocketPromise: Promise<any> | null = null;
let logRocketInitialized = false;

// Simple check for LogRocket ID
const hasLogRocketId = (): boolean => {
  const logRocketId = process.env.REACT_APP_LOGROCKET_ID;
  return !!(logRocketId && logRocketId.trim() !== '');
};

// Sanitize request data
const sanitizeRequestData = (request: any): any => {
  if (request.headers) {
    delete request.headers.authorization;
    delete request.headers.cookie;
    delete request.headers['x-api-key'];
  }

  if (request.body && typeof request.body === 'string') {
    request.body = sanitizeBodyString(request.body);
  }

  return request;
};

// Sanitize response data
const sanitizeResponseData = (response: any): any => {
  if (response.body && typeof response.body === 'string') {
    response.body = sanitizeBodyString(response.body);
  }

  return response;
};

// Helper to sanitize JSON or string body
const sanitizeBodyString = (body: string): string => {
  try {
    const parsedBody = JSON.parse(body);
    sanitizeJsonObject(parsedBody);
    return JSON.stringify(parsedBody);
  } catch (e) {
    return applySensitiveDataRegex(body);
  }
};

// Check if a key should be sanitized
const isSensitiveKey = (key: string): boolean => {
  const lowerKey = key.toLowerCase();

  // Check for password-related keys
  if (lowerKey.includes('password')) {
    return true;
  }

  // Check for other sensitive patterns
  const sensitivePatterns = [
    'password',
    'pwd',
    'ssn',
    'social_security_number',
    'credit_card',
    'card_number',
    'cvv',
    'social_security',
    'tax_id',
    'ein',
    'routing_number',
    'account_number',
  ];

  return sensitivePatterns.some((pattern) => lowerKey.includes(pattern));
};

// Recursively sanitize JSON object in place
const sanitizeJsonObject = (obj: any): void => {
  if (!obj || typeof obj !== 'object') {
    return;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    obj.forEach((item) => sanitizeJsonObject(item));
    return;
  }

  // Handle objects
  Object.keys(obj).forEach((key) => {
    if (isSensitiveKey(key)) {
      obj[key] = '[REDACTED]';
    } else if (typeof obj[key] === 'object') {
      // Recursively sanitize nested objects
      sanitizeJsonObject(obj[key]);
    }
  });
};

// Apply regex patterns to string
const applySensitiveDataRegex = (text: string): string => {
  return text
    .replace(/\b\d{3}-?\d{2}-?\d{4}\b/g, '[SSN-REDACTED]')
    .replace(/\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g, '[CC-REDACTED]');
};

// Create LogRocket configuration
const createLogRocketConfig = () => ({
  console: {
    shouldAggregateConsoleErrors: true,
    isEnabled: {
      log: false,
      info: false,
      warn: true,
      error: true,
      debug: false,
    },
  },
  network: {
    isEnabled: true,
    requestSanitizer: sanitizeRequestData,
    responseSanitizer: sanitizeResponseData,
  },
});

// Initialize LogRocket
const initLogRocket = async (): Promise<any> => {
  if (logRocketInitialized) {
    return logRocketInstance;
  }

  if (logRocketPromise) {
    return logRocketPromise;
  }

  if (!hasLogRocketId()) {
    console.info('LogRocket ID not found - LogRocket will not be initialized');
    logRocketInitialized = true;
    return null;
  }

  try {
    logRocketPromise = initializeLogRocketModule();
    return await logRocketPromise;
  } catch (error) {
    console.error('Failed to initialize LogRocket:', error);
    logRocketInitialized = true;
    return null;
  }
};

// Separate function to handle the actual module initialization
const initializeLogRocketModule = async () => {
  const logRocket = await import('logrocket');
  const logRocketId = process.env.REACT_APP_LOGROCKET_ID!;

  logRocket.default.init(logRocketId, createLogRocketConfig());

  logRocketInstance = logRocket.default;
  logRocketInitialized = true;

  (window as any).LogRocket = logRocket.default;
  console.info('LogRocket initialized successfully');

  return logRocket.default;
};

// Public API functions
export const identifyUser = async (
  userId: string,
  userInfo: { name: string; email: string }
): Promise<void> => {
  try {
    const logRocket = await initLogRocket();
    if (!logRocket) {
      return;
    }

    const sanitizedUserInfo = {
      name: userInfo.name,
      email: userInfo.email,
    };

    logRocket.identify(userId, sanitizedUserInfo);
  } catch (error) {
    console.error('Failed to identify user with LogRocket:', error);
  }
};

export const isLogRocketAvailable = (): boolean => {
  return logRocketInitialized && logRocketInstance !== null;
};

export const getLogRocketSessionURL = (): string | null => {
  try {
    if (!isLogRocketAvailable()) {
      return null;
    }
    return logRocketInstance.sessionURL || null;
  } catch (error) {
    return null;
  }
};

export const getLogRocketInstance = (): any => {
  return logRocketInstance;
};
